import type { TableColumnsDropdownItem, TableHeaderColumnProps } from '@rio-cloud/rio-uikit/Table';
import type { Driver } from '../../../../../types';

export const DRIVER_TABLE_STORAGE_KEY = 'driver-administration.table';

export const DRIVER_TABLE_COLUMN_KEYS = {
    CARD: 'card',
    DRIVER: 'driver',
    EMAIL: 'email',
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    PHONE: 'phoneNumber',
    STATUS: 'status',
    TAG: 'tags',
    USER: 'user',
} as const;

export type DriverTableColumnKey = (typeof DRIVER_TABLE_COLUMN_KEYS)[keyof typeof DRIVER_TABLE_COLUMN_KEYS];

type DriverTableHeaderConfig = Pick<
    TableHeaderColumnProps<Driver>,
    | 'draggable'
    | 'hideLabel'
    | 'horizontalAlign'
    | 'maxResizeWidth'
    | 'minResizeWidth'
    | 'mobileLabel'
    | 'resizeable'
    | 'sortable'
    | 'width'
>;

type DriverTableDropdownConfig = Pick<TableColumnsDropdownItem, 'disabled'>;

export interface DriverTableColumnConfig extends DriverTableHeaderConfig, DriverTableDropdownConfig {
    key: DriverTableColumnKey;
    label: string;
    visible: boolean;
}

export interface DriverTablePersistedColumnConfig {
    key: DriverTableColumnKey;
    visible: boolean;
    width?: number | string;
}

export const buildDefaultDriverTableColumns = (labels: {
    card: string;
    driver: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    status: string;
    tag: string;
    user: string;
}): DriverTableColumnConfig[] => [
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.DRIVER,
        label: labels.driver,
        maxResizeWidth: 420,
        minResizeWidth: 220,
        mobileLabel: labels.driver,
        resizeable: true,
        sortable: true,
        visible: true,
        width: 280,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.FIRST_NAME,
        label: labels.firstName,
        maxResizeWidth: 320,
        minResizeWidth: 140,
        mobileLabel: labels.firstName,
        resizeable: true,
        sortable: true,
        visible: false,
        width: 180,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.LAST_NAME,
        label: labels.lastName,
        maxResizeWidth: 320,
        minResizeWidth: 140,
        mobileLabel: labels.lastName,
        resizeable: true,
        sortable: true,
        visible: false,
        width: 180,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.CARD,
        label: labels.card,
        maxResizeWidth: 420,
        minResizeWidth: 180,
        mobileLabel: labels.card,
        resizeable: true,
        sortable: true,
        visible: false,
        width: 260,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.EMAIL,
        label: labels.email,
        maxResizeWidth: 360,
        minResizeWidth: 180,
        mobileLabel: labels.email,
        resizeable: true,
        sortable: true,
        visible: true,
        width: 220,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.PHONE,
        label: labels.phone,
        maxResizeWidth: 280,
        minResizeWidth: 140,
        mobileLabel: labels.phone,
        resizeable: true,
        sortable: true,
        visible: true,
        width: 180,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.STATUS,
        label: labels.status,
        maxResizeWidth: 180,
        minResizeWidth: 100,
        mobileLabel: labels.status,
        resizeable: true,
        sortable: true,
        visible: true,
        width: 120,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.USER,
        label: labels.user,
        maxResizeWidth: 360,
        minResizeWidth: 180,
        mobileLabel: labels.user,
        resizeable: true,
        sortable: true,
        visible: true,
        width: 220,
    },
    {
        disabled: false,
        draggable: true,
        key: DRIVER_TABLE_COLUMN_KEYS.TAG,
        label: labels.tag,
        maxResizeWidth: 360,
        minResizeWidth: 180,
        mobileLabel: labels.tag,
        resizeable: true,
        sortable: false,
        visible: true,
    },
];

export const getDriverTableStorageKey = (accountId: string | null | undefined) => {
    const accountPrefix = accountId?.slice(0, 6) ?? 'default';

    return `${DRIVER_TABLE_STORAGE_KEY}:${accountPrefix}`;
};

export const mergeDriverTableColumns = (
    defaultColumns: DriverTableColumnConfig[],
    persistedColumns: DriverTablePersistedColumnConfig[] | undefined
) => {
    if (!persistedColumns?.length) {
        return defaultColumns;
    }

    const hasCombinedDriverColumn = persistedColumns.some(column => column.key === DRIVER_TABLE_COLUMN_KEYS.DRIVER);
    const legacyCombinedDriverColumns: DriverTableColumnKey[] = [
        DRIVER_TABLE_COLUMN_KEYS.FIRST_NAME,
        DRIVER_TABLE_COLUMN_KEYS.LAST_NAME,
        DRIVER_TABLE_COLUMN_KEYS.CARD,
        DRIVER_TABLE_COLUMN_KEYS.USER,
    ];

    const normalizedPersistedColumns: DriverTablePersistedColumnConfig[] = hasCombinedDriverColumn
        ? persistedColumns
        : (() => {
              const driverColumnSource =
                  persistedColumns.find(column => column.key === DRIVER_TABLE_COLUMN_KEYS.FIRST_NAME) ??
                  persistedColumns.find(column => column.key === DRIVER_TABLE_COLUMN_KEYS.LAST_NAME) ??
                  persistedColumns.find(column => column.key === DRIVER_TABLE_COLUMN_KEYS.CARD) ??
                  persistedColumns.find(column => column.key === DRIVER_TABLE_COLUMN_KEYS.USER);
              const driverVisible = persistedColumns.some(
                  column => legacyCombinedDriverColumns.includes(column.key) && column.visible
              );
              const nextColumns = persistedColumns.map(column => {
                  if (!legacyCombinedDriverColumns.includes(column.key)) {
                      return column;
                  }

                  return {
                      ...column,
                      visible: false,
                  };
              });
              const driverColumn: DriverTablePersistedColumnConfig = {
                  key: DRIVER_TABLE_COLUMN_KEYS.DRIVER,
                  visible: driverVisible,
                  width: driverColumnSource?.width,
              };

              if (!driverColumnSource) {
                  return [driverColumn, ...nextColumns];
              }

              const sourceIndex = persistedColumns.findIndex(column => column.key === driverColumnSource.key);

              return [...nextColumns.slice(0, sourceIndex), driverColumn, ...nextColumns.slice(sourceIndex)];
          })();

    const persistedColumnsByKey = new Map(normalizedPersistedColumns.map(column => [column.key, column]));
    const orderedPersistedKeys = normalizedPersistedColumns.map(column => column.key);
    const missingDefaultColumns = defaultColumns
        .filter(column => !persistedColumnsByKey.has(column.key))
        .map(column => column.key);

    return orderedPersistedKeys.concat(missingDefaultColumns).flatMap(key => {
        const defaultColumn = defaultColumns.find(column => column.key === key);
        if (!defaultColumn) {
            return [];
        }

        const persistedColumn = persistedColumnsByKey.get(key);
        return [
            {
                ...defaultColumn,
                visible: persistedColumn?.visible ?? defaultColumn.visible,
                width: persistedColumn?.width ?? defaultColumn.width,
            },
        ];
    });
};

export const normalizeDriverTableColumnsForStorage = (columns: DriverTableColumnConfig[]) =>
    columns.map(column => {
        if (column.width === undefined) {
            return {
                key: column.key,
                visible: column.visible,
            };
        }

        return {
            key: column.key,
            visible: column.visible,
            width: column.width,
        };
    });

export const restoreDriverTableColumnOrder = (
    columns: DriverTableColumnConfig[],
    columnKey: DriverTableColumnKey,
    defaultColumns: DriverTableColumnConfig[]
) => {
    const targetColumn = columns.find(column => column.key === columnKey);

    if (!targetColumn) {
        return columns;
    }

    const columnsWithoutTarget = columns.filter(column => column.key !== columnKey);
    const defaultOrder = defaultColumns.map(column => column.key);
    const targetDefaultIndex = defaultOrder.indexOf(columnKey);

    if (targetDefaultIndex === -1) {
        return columns;
    }

    for (let index = targetDefaultIndex - 1; index >= 0; index -= 1) {
        const previousColumnIndex = columnsWithoutTarget.findIndex(column => column.key === defaultOrder[index]);

        if (previousColumnIndex !== -1) {
            return [
                ...columnsWithoutTarget.slice(0, previousColumnIndex + 1),
                targetColumn,
                ...columnsWithoutTarget.slice(previousColumnIndex + 1),
            ];
        }
    }

    for (let index = targetDefaultIndex + 1; index < defaultOrder.length; index += 1) {
        const nextColumnIndex = columnsWithoutTarget.findIndex(column => column.key === defaultOrder[index]);

        if (nextColumnIndex !== -1) {
            return [
                ...columnsWithoutTarget.slice(0, nextColumnIndex),
                targetColumn,
                ...columnsWithoutTarget.slice(nextColumnIndex),
            ];
        }
    }

    return columnsWithoutTarget.concat(targetColumn);
};

export const areDriverTableColumnsDefault = (
    columns: DriverTableColumnConfig[],
    defaultColumns: DriverTableColumnConfig[]
) => {
    const normalizedColumns = JSON.stringify(normalizeDriverTableColumnsForStorage(columns));
    const normalizedDefaults = JSON.stringify(normalizeDriverTableColumnsForStorage(defaultColumns));

    return normalizedColumns === normalizedDefaults;
};
